import { FC } from 'react';
import { DateTimeInput, Modal } from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { Stack } from '@mui/material';
import { useAdminEditDepositEvent } from 'store/admin-client-deposits';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { usePredefinedToasts } from 'utils/Toast';

interface Values {
  from: string;
  to: string | null;
}

interface Props extends ModalProps {
  eventId: string;
  initialValues: {
    from: string;
    to: string | null;
  };
}

export const EditDepositEventModal: FC<Props> = ({
  eventId,
  initialValues,
  closeModal,
}) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<Values>({
    defaultValues: initialValues,
  });

  const { mutate: editDepositEvent, isLoading: isUpdating } =
    useAdminEditDepositEvent();

  const onSubmit: SubmitHandler<Values> = (values) => {
    editDepositEvent(
      {
        eventId,
        from: values.from,
        to: values.to ?? undefined,
      },
      {
        onSuccess: () => {
          predefinedToasts.success.updated();
          closeModal({ action: 'CLOSE' });
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('alternatives.dateRange')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal({ action: 'CLOSE' }),
            label: t('common.close'),
          },
          {
            variant: 'primary',
            label: t('common.save'),
            type: 'submit',
            disabled: !isDirty || isUpdating,
          },
        ]}
      >
        <Stack gap={4}>
          <Stack gap={2}>
            <Controller
              name="from"
              control={control}
              render={({ field: { name, value, onChange, ref } }) => (
                <DateTimeInput
                  label={t('common.from')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  required
                  ref={ref}
                />
              )}
            />
            <Controller
              name="to"
              control={control}
              render={({ field: { name, value, onChange, ref } }) => (
                <DateTimeInput
                  label={t('common.to')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  required
                  ref={ref}
                />
              )}
            />
          </Stack>
        </Stack>
      </Modal.Content>
    </form>
  );
};
